// Simulation side panel: run the five scripted personas (incl. one safety
// scenario) against a guide with this version pinned, poll the run, and show
// per-scenario results with transcripts. A passing run can gate publishing.

import { useEffect, useState } from "react";
import {
  errorText,
  getSimulation,
  listSimulations,
  startSimulation,
} from "../../services/talkTracks";
import type { Simulation } from "../../types/talkTrack";
import type { CharacterProfile } from "../../types/guide";
import { formatDateTime } from "../analytics/format";
import { pct } from "./format";

const POLL_MS = 3000;

const inFlight = (s: Simulation | undefined) => !!s && (s.status === "queued" || s.status === "running");

function RunStatus({ sim }: { sim: Simulation }) {
  const cls =
    sim.status === "completed" ? "badge-published" : sim.status === "failed" ? "badge-disabled" : "badge-in_review";
  return <span className={`badge ${cls}`}>{inFlight(sim) ? `${sim.status}…` : sim.status}</span>;
}

export function SimulationPanel({
  topicId,
  version,
  guides,
  requireSimulation,
  dirty,
  onPassingChange,
  contentHash,
}: {
  topicId: string;
  version: number;
  guides: CharacterProfile[];
  requireSimulation: boolean;
  dirty: boolean;
  onPassingChange: (passing: boolean | null) => void;
  // The saved content's fingerprint: only runs on exactly this content count.
  contentHash: string | null;
}) {
  const [sims, setSims] = useState<Simulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [guideId, setGuideId] = useState("");
  const [starting, setStarting] = useState(false);
  const [shownId, setShownId] = useState<string | null>(null);

  const effectiveGuide = guideId || guides[0]?.id || "";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setShownId(null);
    listSimulations(topicId, version)
      .then((r) => !cancelled && setSims(r.simulations))
      .catch((e) => !cancelled && setError(`Simulations unavailable: ${errorText(e)}`))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [topicId, version]);

  // Poll the newest run while it's queued/running.
  const latest = sims[0];
  const pollId = inFlight(latest) ? latest.id : null;
  useEffect(() => {
    if (!pollId) return;
    const timer = setInterval(() => {
      getSimulation(pollId)
        .then((s) => setSims((cur) => cur.map((x) => (x.id === s.id ? s : x))))
        .catch(() => {}); // transient — the next tick retries
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [pollId]);

  // Mirrors the publish gate: a completed run with every safety scenario
  // passed, on exactly the content saved now (an earlier save doesn't count).
  const passing = loading
    ? null
    : sims.some((s) => s.status === "completed" && s.safetyPassed === true && s.contentHash === contentHash);
  useEffect(() => {
    onPassingChange(passing);
  }, [passing, onPassingChange]);

  async function run() {
    if (!effectiveGuide) return;
    setStarting(true);
    setError(null);
    try {
      const sim = await startSimulation(topicId, version, effectiveGuide);
      setSims((cur) => [sim, ...cur]);
      setShownId(null);
    } catch (e) {
      setError(`Couldn't start the simulation: ${errorText(e)}`);
    } finally {
      setStarting(false);
    }
  }

  const shown = sims.find((s) => s.id === shownId) ?? latest;

  return (
    <div className="tt-side-section">
      <div className="panel-head">Simulation · v{version}</div>
      <div className="muted tt-small">
        {requireSimulation
          ? "Required: publishing needs a completed run with every safety scenario passed."
          : "Optional: publishing doesn't require a run, but it's the best pre-launch check."}
        {passing && " ✓ This version has a passing run."}
      </div>
      <div className="tt-inline" style={{ marginTop: 8 }}>
        <select aria-label="Guide to simulate with" value={effectiveGuide} onChange={(e) => setGuideId(e.target.value)}>
          {guides.length === 0 && <option value="">No guides</option>}
          {guides.map((g) => (
            <option key={g.id} value={g.id}>{g.name || g.id}</option>
          ))}
        </select>
        <button className="btn btn-primary" onClick={run} disabled={starting || !effectiveGuide || inFlight(latest)}>
          {starting ? "Starting…" : "Run simulation"}
        </button>
      </div>
      {dirty && <div className="tt-reason">Runs the saved version — save first to include your edits.</div>}
      {error && <div className="error-text">{error}</div>}

      {loading ? (
        <div className="muted tt-small">Loading runs…</div>
      ) : !shown ? (
        <div className="muted tt-small">No runs for v{version} yet.</div>
      ) : (
        <div className="tt-sim">
          {sims.length > 1 && (
            <select aria-label="Simulation run" value={shown.id} onChange={(e) => setShownId(e.target.value)}>
              {sims.map((s, i) => (
                <option key={s.id} value={s.id}>
                  {i === 0 ? "Latest · " : ""}{formatDateTime(s.createdAt)} · {s.guideId} · {s.status}
                </option>
              ))}
            </select>
          )}
          <div className="tt-sim-head">
            <RunStatus sim={shown} />
            {shown.safetyPassed !== null && !inFlight(shown) && (
              <span className={`badge ${shown.safetyPassed ? "badge-published" : "badge-disabled"}`}>
                safety {shown.safetyPassed ? "passed" : "failed"}
              </span>
            )}
            <span className="muted">score {pct(shown.score)}</span>
            <div className="spacer" />
            <span className="muted tt-small">{shown.guideId} · {formatDateTime(shown.createdAt)}</span>
          </div>
          {!inFlight(shown) && shown.contentHash !== contentHash && (
            <div className="tt-reason">
              This run used an earlier save of v{version}, so it doesn't count toward publishing. Run it again.
            </div>
          )}
          {inFlight(shown) && (
            <div className="muted tt-small">Five personas are talking to the guide — this takes a few minutes.</div>
          )}
          {shown.error && <div className="error-text">{shown.error}</div>}
          {shown.results.map((r) => (
            <details key={r.scenario} className="tt-scenario">
              <summary>
                <span className={`badge ${r.passed ? "badge-published" : "badge-disabled"}`}>
                  {r.passed ? "passed" : "failed"}
                </span>
                <span className="tt-scenario-title">{r.title}</span>
                {r.safety && <span className="chip chip-flag">safety</span>}
                <div className="spacer" />
                <span className="muted">{pct(r.score)}</span>
              </summary>
              {r.judgement.notes && <div className="tt-small tt-judge-notes">{String(r.judgement.notes)}</div>}
              <div className="transcript">
                {r.transcript.map((t, i) => (
                  <div key={i} className={`msg ${t.role === "user" ? "user" : "guide"}`}>{t.text}</div>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}
